import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center md:gap-2 gap-1 whitespace-nowrap rounded-pill md:text-sm text-xs font-medium ring-offset-background transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-blue-600 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-ds-green-600 text-white font-semibold hover:bg-ds-green-500 hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(34,197,94,0.3)]",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-ds-border-200 bg-white text-navy-900 hover:border-ds-blue-600 hover:bg-ds-blue-50/30",
        secondary:
          "border border-ds-border-200 bg-white text-navy-900 hover:border-ds-border-300 hover:bg-ds-surface-50",
        ghost: "hover:bg-ds-surface-50 hover:text-navy-900",
        ghostDanger: "hover:bg-destructive hover:text-destructive-foreground",
        link: "text-ds-blue-600 underline-offset-4 hover:underline",
        hero: "bg-ds-green-600 text-white font-semibold hover:bg-ds-green-500 hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(34,197,94,0.3)]",
        success:
          "bg-ds-green-600 text-white font-semibold hover:bg-ds-green-500 hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(34,197,94,0.3)]",
      },
      size: {
        default: "h-10 px-5 py-2",
        xs: "h-7 px-3 text-xs [&_svg]:size-3.5",
        sm: "h-9 px-4",
        lg: "md:h-12 h-10 md:px-8 px-5",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
